import {ControlModel, UserRole} from '../types/control';
import {IntercomModel} from '../types/intercom';
import {
    controlSendErrorAction,
    mediaStatusUpdatedAction,
    setBellStatusAction,
    setIntercomInfoAction,
    setIntercomStatusAction,
    inputSendErrorAction,
    entityDeletedAction,
    entityCreatedAction,
    entityUpdatedAction,
    setScreenSaverStatusAction,
    setPasswordAction,
    setVersionAction,
    setRSSIAction,
    setSupervisorConfigUpdateAction,
    setEnergyUpdated,
    setMessageAction,
    setMessageNotification,
    setReadAllTempFunc,
    setUserResetPassword,
    setSoundPlayingStatusAction,
    setSoundStateAction,
    setTouchMessageAction,
    setPasswordActionV2,
    setPingTestAction,
    setTraceRouteAction,
    setMousePointerAction,
    setHomepageItemsActions,
    setTemporaryUserAction,
    setMatchStateAction,
    setResetCountdown,
    setDetailedRFTest,
} from '../store/control/actions';
import {addUARTMessageAction} from '../store/debug/actions';
import {dateTimeAction, setAuthenticationAction, setSelectedBackgroundAction} from '../store/ui/actions';
import {Dispatch} from 'redux';
import {removeSession} from '../utils/session-storage';
import {addRawDeviceAction} from '../store/setup/actions';
import {RawDevice} from '../store/setup/state';
import {EnergyMonitor, MessageListType, MessageNotification, UserResetPassword, SoundState, SoundPlayingStatus, DetailedRFTestResponse} from '../store/control/state'
import {setTemperatureAction} from '../store/temperature/action';
import {TemperatureMessage} from '../store/temperature/state';
import {clearUserAction} from '../store/user/action';
import {DateTimeModel, MediaFileModel} from '../store/ui/state';

export class ClientTask {
    constructor(private dispatch: Dispatch) {
    }

    onControlSendError(controlId: string) {
        this.dispatch(controlSendErrorAction(controlId, true));
    }

    onInputSendError(inputId: string) {
        this.dispatch(inputSendErrorAction(inputId, true));
    }

    onUartMessage({message, type, time}: { message: string; type: string; time: string; }) {
        this.dispatch(addUARTMessageAction(message, type, time));
    }

    onMediaStatusUpdated(message: string) {
        this.dispatch(mediaStatusUpdatedAction(message));
    }

    onIntercomStatusChanged({
                                status,
                                info,
                                id,
                                name,
                                call_time
                            }: { status: string, info?: IntercomModel, id: string, name: string, call_time: number }) {
        if (info) {
            this.dispatch(setIntercomInfoAction(info));
        }

        let isIncomingCall = true;
        if (status === 'outgoing') {
            status = 'incoming';
            isIncomingCall = false;
        }
        if (status !== "incoming") {
            call_time = 0
        }
        this.dispatch(setIntercomStatusAction(status, isIncomingCall, id, name, call_time))
    }

    onBellRinging(status: string) {
        this.dispatch(setBellStatusAction('incoming'));
    }

    onEntityDeleted({id, type}: { id: string, type: string }) {
        this.dispatch(entityDeletedAction(id, type));
    }

    onEntityCreated({entity, type}: { type: string, entity: unknown }) {
        if (type === 'control') {
            const control = entity as ControlModel;
            if (control.temperature_settings != null) {
                control.temperature_settings.whole = control.temperature_settings.whole || 14;
                if (control.temperature_settings.whole === 14) {
                    control.temperature_settings.fraction = 5;
                }
            }
        }

        if (type === 'mediaFile') {
            const background = entity as MediaFileModel;
            if (background.type === 'background') {
                this.dispatch(setSelectedBackgroundAction(background.id));
            }
        }

        this.dispatch(entityCreatedAction(entity, type));
    }

    onEntityUpdated({entity, type}: { type: string, entity: { id: string, [key: string]: unknown } }) {
        if (window.location.pathname.includes('/settings/input/edit/')) {
            return
        }
        if (type === 'control') {
            // @ts-ignore
            const control = entity as ControlModel;
            if (control.temperature_settings != null) {
                control.temperature_settings.whole = control.temperature_settings.whole || 14;
                if (control.temperature_settings.whole === 14) {
                    control.temperature_settings.fraction = 5;
                }
            }
        }
        this.dispatch(entityUpdatedAction(entity, type));
    }

    onAuthenticated(role: UserRole) {
        this.dispatch(setAuthenticationAction(role));

        if (role === 'default') {
            removeSession();
            this.dispatch(clearUserAction());
        }
    }

    onDeviceRevealed(deviceRevealMessage: RawDevice) {
        this.dispatch(addRawDeviceAction(deviceRevealMessage));
    }

    onTemperatureUpdated(temperatureMessage: TemperatureMessage) {
        this.dispatch(setTemperatureAction(temperatureMessage.icon, temperatureMessage.temperature));
    }

    onCheckedAlarmPassword(passwordStatus: boolean) {
        this.dispatch(setPasswordAction(passwordStatus));
    }

    onCheckedAlarmPasswordV2(passwordStatusV2: boolean) {
        this.dispatch(setPasswordActionV2(passwordStatusV2));
    }

    onScreenSaverStart(slide: string) {
        this.dispatch(setScreenSaverStatusAction(slide))
    }

    onVersionQuery(versionStatus: string) {
        this.dispatch(setVersionAction(versionStatus))
    }

    onRSSIQuery(rssiStatus: string) {
        this.dispatch(setRSSIAction(rssiStatus))
    }

    onEnergyUpdated(energyUpdated: EnergyMonitor) {
        this.dispatch(setEnergyUpdated(energyUpdated))
    }

    onMessageList(messageList: MessageListType) {
        this.dispatch(setMessageAction(messageList))
    }

    onNewMessageFromConcierge(messageNotification: MessageNotification) {
        this.dispatch(setMessageNotification(messageNotification))
    }

    onReadAllTempFunc(readAllTempFunc: string) {
        this.dispatch(setReadAllTempFunc(readAllTempFunc))
    }

    onSupConfigUpdated({sup_config, type}: {type: string; sup_config: {id: string; [key: string]: unknown}}) {
        this.dispatch(setSupervisorConfigUpdateAction(sup_config, type))
    }

    onResetUserPassword(userResetPassword: UserResetPassword) {
        this.dispatch(setUserResetPassword(userResetPassword))
    }

    onPlayingStatusUpdated(soundPlayingStatus: SoundPlayingStatus) {
        this.dispatch(setSoundPlayingStatusAction(soundPlayingStatus))
    }

    onSoundStateUpdated(soundState: SoundState) {
        this.dispatch(setSoundStateAction(soundState))
    }

    onTouchMessage(touchMessageStatus: {message: boolean}) {
        if(touchMessageStatus?.message && (window.location.pathname === "/digital-clock" || window.location.pathname === "/slider" || window.location.pathname === "/analog-clock")){
            this.dispatch(setScreenSaverStatusAction(""))
        }
        this.dispatch(setTouchMessageAction(touchMessageStatus))
    }

    onSendPing(pingTest: string) {
        this.dispatch(setPingTestAction(pingTest))
    }

    onSendTraceRoute(traceRouteTest: string) {
        this.dispatch(setTraceRouteAction(traceRouteTest))
    }

    onMouseCursorStatusUpdated(mousePointerStatus: boolean) {
        this.dispatch(setMousePointerAction(mousePointerStatus))
    }

    onHomepageItemVisibilityUpdated(favoritesPage: boolean) {
        this.dispatch(setHomepageItemsActions(favoritesPage))
    }

    onSendMatchState(matchState: string) {
        this.dispatch(setMatchStateAction(matchState))
    }

    onUserSessionExpired(temporaryUserStatus: string) {
        this.dispatch(setTemporaryUserAction(temporaryUserStatus))
    }

    onSendWsMessage(somResetCountdown: string) {
        this.dispatch(setResetCountdown(somResetCountdown))
    }

    onDetailRfTestResponse(detailedRFTestResponse: DetailedRFTestResponse) {
        this.dispatch(setDetailedRFTest(detailedRFTestResponse))
    }
  
    onSendDateTime(dateTime: DateTimeModel) {
        this.dispatch(dateTimeAction(dateTime))
    }
}
